import { useEffect, useRef } from "react";
import useCountDown from "react-countdown-hook";
import { useRecoilState } from "recoil";
import { useQuery } from "@apollo/react-hooks";
import { OC_GET_VERSION } from "oc/graphql";
import { useOcPolling } from "oc/hooks";
import { ocCurrentVersionState, ocShowDialogState } from "states";

/**
 * Custom hook to produce all data for refresh process
 * @returns {Object}
 */
export const useOcGetVersion = () => {
  const INITIAL_COUNTER_TIME = 5 * 60 * 1000; // initial time in milliseconds, defaults to 60000
  const COUNTER_INTERVAL = 1000; // interval to change remaining time amount, defaults to 1000

  const [timeToRefresh, { start }] = useCountDown(
    INITIAL_COUNTER_TIME,
    COUNTER_INTERVAL,
  );

  const [currentVersion, setCurrentVersion] = useRecoilState(
    ocCurrentVersionState,
  );

  const [showRefreshDialog, setShowRefreshDialog] =
    useRecoilState(ocShowDialogState);

  const previousVersionRef = useRef<string | null>(null);

  const { data, loading, refetch } = useQuery(OC_GET_VERSION, {
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    pollInterval: useOcPolling(1000 * 60 * 5),
  });

  // Amikor a tab újra aktívvá válik, azonnal lekérdezzük a verziót
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refetch();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refetch]);

  // Handle version change detection via useEffect
  useEffect(() => {
    if (data?.getVersion && !loading) {
      const newVersion = data.getVersion;

      if (!currentVersion) {
        setCurrentVersion(newVersion);
        previousVersionRef.current = newVersion;
      } else if (currentVersion && newVersion !== currentVersion && newVersion !== previousVersionRef.current) {
        setShowRefreshDialog(true);
        start();
        previousVersionRef.current = newVersion;
      }
    }
  }, [data, loading, currentVersion, setCurrentVersion, setShowRefreshDialog, start]);

  const formatTime = (timeInMs: number) => {
    let seconds: string = Math.floor((timeInMs / 1000) % 60).toString();
    let minutes = Math.trunc(timeInMs / 1000 / 60);

    //minutes = minutes < 1 ? "0" : minutes < 10 ? `0${minutes}` : minutes;
    seconds =
      Number.parseInt(seconds) < 1
        ? "00"
        : Number.parseInt(seconds) < 10
          ? `0${seconds}`
          : seconds;
    return `${minutes}:${seconds}`;
  };

  const onRefresh = () => {
    setShowRefreshDialog(false);
    setCurrentVersion(data?.getVersion);
    window.location.reload();
  };

  if (
    data &&
    currentVersion &&
    currentVersion !== data?.getVersion &&
    timeToRefresh === 1000
  ) {
    onRefresh();
  }

  return {
    hasToRefresh: data && currentVersion !== data?.getVersion,
    timeToRefreshValue: timeToRefresh,
    timeToRefresh: formatTime(timeToRefresh),
    startCounter: start,
    showRefreshDialog,
    setShowRefreshDialog,
    onRefresh,
    currentVersion,
  };
};

export default useOcGetVersion;
